'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import type { ScheduleDay } from '@/lib/hours/scheduleSlots'
import { formatTime12h } from './OpenStatusBadge'

export type ScheduleMode = 'asap' | 'scheduled'

type Props = {
  days: ScheduleDay[]
  mode: ScheduleMode
  scheduledFor: string | null
  /** Si false, el modo ASAP queda deshabilitado (local cerrado ahora). */
  asapAvailable: boolean
  accentColor: string
  onChangeMode: (mode: ScheduleMode) => void
  onChangeSlot: (iso: string | null) => void
}

export function SchedulePicker({
  days,
  mode,
  scheduledFor,
  asapAvailable,
  accentColor,
  onChangeMode,
  onChangeSlot,
}: Props) {
  const t = useTranslations('public.microsite')
  const [selectedDateKey, setSelectedDateKey] = useState<string>(days[0]?.dateKey ?? '')

  // Mantener el día seleccionado válido cuando los slots cambian (cada minuto).
  useEffect(() => {
    if (days.length === 0) return
    if (!days.some((d) => d.dateKey === selectedDateKey)) {
      setSelectedDateKey(days[0].dateKey)
    }
  }, [days, selectedDateKey])

  // Al entrar en modo "programar" sin slot elegido, preseleccionar el primero.
  useEffect(() => {
    if (mode !== 'scheduled' || scheduledFor || days.length === 0) return
    const day = days.find((d) => d.dateKey === selectedDateKey) ?? days[0]
    onChangeSlot(day.slots[0]?.iso ?? null)
  }, [mode, scheduledFor, days, selectedDateKey, onChangeSlot])

  const currentDay = days.find((d) => d.dateKey === selectedDateKey) ?? days[0]

  function dayLabel(d: ScheduleDay): string {
    // "Lunes 06-18" — el día localizado + fecha corta (MM-DD).
    return `${t(`days.${d.dayKey}`)} ${d.dateKey.slice(5)}`
  }

  const baseBtn =
    'flex-1 rounded-full py-2.5 text-sm font-semibold transition border'
  const activeStyle = { backgroundColor: accentColor, color: '#fff', borderColor: accentColor }
  const inactiveCls = 'bg-white text-gray-700 border-gray-200'

  return (
    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {t('schedule.when')}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => asapAvailable && onChangeMode('asap')}
          disabled={!asapAvailable}
          className={`${baseBtn} ${mode === 'asap' ? '' : inactiveCls} disabled:opacity-40 disabled:cursor-not-allowed`}
          style={mode === 'asap' ? activeStyle : undefined}
        >
          {t('schedule.asap')}
        </button>
        <button
          type="button"
          onClick={() => onChangeMode('scheduled')}
          disabled={days.length === 0}
          className={`${baseBtn} ${mode === 'scheduled' ? '' : inactiveCls} disabled:opacity-40 disabled:cursor-not-allowed`}
          style={mode === 'scheduled' ? activeStyle : undefined}
        >
          {t('schedule.later')}
        </button>
      </div>

      {mode === 'scheduled' && days.length > 0 && currentDay && (
        <div className="flex gap-2">
          <select
            value={selectedDateKey}
            onChange={(e) => {
              setSelectedDateKey(e.target.value)
              const d = days.find((x) => x.dateKey === e.target.value)
              onChangeSlot(d?.slots[0]?.iso ?? null)
            }}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-base text-gray-900 capitalize focus:outline-none"
          >
            {days.map((d) => (
              <option key={d.dateKey} value={d.dateKey} className="capitalize">
                {dayLabel(d)}
              </option>
            ))}
          </select>
          <select
            value={scheduledFor ?? ''}
            onChange={(e) => onChangeSlot(e.target.value || null)}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-base text-gray-900 focus:outline-none"
          >
            {currentDay.slots.map((s) => (
              <option key={s.iso} value={s.iso}>
                {formatTime12h(s.label)}
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === 'scheduled' && days.length === 0 && (
        <p className="text-xs text-gray-500">{t('schedule.noSlots')}</p>
      )}
    </div>
  )
}
